import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * `React.lazy` odporny na nieaktualne kawałki kodu po wdrożeniu (v0.123.0).
 *
 * ## Problem
 *
 * Vite tnie aplikację na kawałki z hashem w nazwie (`ObjectNotesPanel-E6t_TuLd.js`).
 * Po wdrożeniu hashe się zmieniają, a stare pliki znikają z serwera. Karta,
 * którą ktoś miał otwartą przed wdrożeniem, nadal trzyma STARY indeks — więc
 * przy wejściu w sekcję ładowaną leniwie prosi o plik, którego już nie ma:
 *
 * > Failed to fetch dynamically imported module: .../ObjectNotesPanel-E6t_TuLd.js
 *
 * Użytkownik widział wtedy ekran „Coś poszło nie tak" i musiał sam wpaść na to,
 * żeby odświeżyć stronę. Aplikacja ma 57 leniwych tras, a wdrożenia bywają
 * kilka razy dziennie — to nie był przypadek brzegowy.
 *
 * ## Rozwiązanie
 *
 * Przy nieudanym imporcie odświeżamy stronę RAZ. Przeglądarka pobiera świeży
 * indeks z aktualnymi hashami i trafia tam, gdzie użytkownik chciał wejść.
 *
 * Znacznik w `sessionStorage` chroni przed pętlą przeładowań: jeśli po
 * odświeżeniu import nadal pada, to znaczy, że przyczyna jest inna (awaria
 * sieci, wadliwy build) i błąd musi dotrzeć do `ErrorBoundary` zamiast
 * zapętlać stronę. Znacznik kasuje się przy pierwszym udanym imporcie.
 */
// `ComponentType<any>` odwzorowuje ograniczenie SAMEGO `React.lazy` — węższy
// typ (`unknown`, `never`) nie przechodzi przez jego sygnaturę, a szerszy nie
// istnieje. To jedyne miejsce, gdzie `any` jest tu potrzebne; propsy konkretnego
// komponentu i tak zachowuje generyk `T`.
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async (): Promise<{ default: T }> => {
    try {
      const mod = await factory();
      // Udany import — kolejna awaria zasługuje na własną szansę odświeżenia.
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      if (!isStaleChunkError(err)) throw err;

      if (sessionStorage.getItem(RELOAD_FLAG)) {
        // Odświeżenie już nie pomogło — nie zapętlamy, oddajemy błąd wyżej.
        throw err;
      }

      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();

      // Reload jest asynchroniczny; obietnica, która nigdy się nie rozstrzyga,
      // trzyma Suspense na spinnerze zamiast mignąć błędem tuż przed przeładowaniem.
      return new Promise<never>(() => {});
    }
  });
}

const RELOAD_FLAG = 'pinveo:chunk-reload';

/**
 * Czy błąd wygląda na brakujący kawałek kodu po wdrożeniu.
 *
 * Komunikaty różnią się między przeglądarkami — Chrome mówi „Failed to fetch
 * dynamically imported module", Safari „Importing a module script failed",
 * Firefox „error loading dynamically imported module". Dlatego dopasowujemy
 * kilka wzorców zamiast jednego; pomyłka w drugą stronę (potraktowanie innego
 * błędu jako nieaktualnego kawałka) kosztuje jedno zbędne odświeżenie, więc
 * wolimy być pobłażliwi.
 */
function isStaleChunkError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return (
    message.includes('dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Failed to fetch') ||
    message.includes('error loading dynamically imported module')
  );
}

/**
 * Ta sama obsługa dla nieudanego PREloadu.
 *
 * Vite wstępnie pobiera kawałki dla tras, w które użytkownik dopiero może
 * wejść. Taka awaria nie przechodzi przez `lazy()`, tylko emituje zdarzenie
 * `vite:preloadError` — bez tego nasłuchu odświeżenie nastąpiłoby dopiero
 * przy faktycznym wejściu w sekcję.
 */
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    event.preventDefault();
    sessionStorage.setItem(RELOAD_FLAG, '1');
    window.location.reload();
  });
}
